import { useEffect, useMemo, useRef } from 'react';

import { skuRef } from '../../../helpers/purchasing';
import { useAuth } from '../../../providers/AuthProvider';
import { useCheckoutCtx } from '../../../providers/CheckoutProvider';
import { useProducts } from '../../../providers/ProductProvider';
import { GeneratedCreateSubscriptionsInput } from '../../../types/generated';
import { captureError } from '../../../utils/capture';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { useSubscriptionsManager } from '../../auth/hooks/useSubscriptionsManager';

const DRAW_VARIANT_TITLE = 'Draw';

const subscriptionKey = (subscription: GeneratedCreateSubscriptionsInput) => {
  return `${subscription.type}:${subscription.objectId}`;
};

export const useCheckoutSubscriptions = () => {
  const { isLoggedIn } = useAuth();
  const { getProduct } = useProducts();
  const { subscribe } = useSubscriptionsManager();
  const { isPreOrderFlow, selectedWorkIds } = useCheckoutCtx();

  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;
  const requestedKey = useRef<string | null>(null);

  const subscriptions = useMemo(() => {
    const bySubscription = new Map<string, GeneratedCreateSubscriptionsInput>();

    selectedWorkIds.forEach((workId) => {
      const product = getProduct(workId)?.backendProduct;
      if (!product) return;

      const drawVariant = product.variants.find((variant) => {
        return variant.title === DRAW_VARIANT_TITLE;
      });

      buildSubscriptions({
        artists: product.artists.map((artist) => ({
          name: artist.name,
          internalId: artist.id,
        })),
        isPreOrder: isPreOrderFlow,
        productId: product.shopifyId,
        productRef: skuRef(drawVariant?.sku ?? ''),
        productTitle: product.title,
      }).forEach((subscription) => {
        if (!subscription.objectId) return;
        bySubscription.set(subscriptionKey(subscription), subscription);
      });
    });

    return [...bySubscription.values()];
  }, [getProduct, isPreOrderFlow, selectedWorkIds]);

  useEffect(() => {
    if (!isLoggedIn) {
      requestedKey.current = null;
      return;
    }

    if (!subscriptions.length) return;

    const key = subscriptions.map(subscriptionKey).sort().join('|');
    if (requestedKey.current === key) return;
    requestedKey.current = key;

    subscribeRef.current(subscriptions).catch((error) => {
      requestedKey.current = null;
      captureError(error as Error);
    });
  }, [isLoggedIn, subscriptions]);
};
